import { useEffect, useMemo, useState } from "react";
import { database, ref, onValue } from "../../config/firebase";

// A regeneration should take seconds; a node older than this is a leftover
// from a crashed run and must not freeze the UI on "Regenerating…" forever.
const STALE_MS = 10 * 60 * 1000;

/**
 * REGENERATE SINGLE EMAIL (2026-08-13): live "which steps are regenerating"
 * for one campaign, straight from Firebase RTDB.
 *
 * The backend sets campaign_regen/{campaignId}/{rowId} = { email,
 * cam_step_id, step_number, started_at } while a regeneration is running and
 * removes it when it finishes (success or failure). Because the flag lives
 * server-side, "Regenerating…" survives a closed drawer or a full page
 * refresh — the UI simply reflects the node, exactly like campaign_progress
 * does for personalization.
 *
 * Returns a Set of `${cam_step_id}|${email.toLowerCase()}` keys — that pair is
 * what both the content drawer and the step cards know about a step.
 */
const useRegeneratingSteps = (campaignId) => {
  const [entries, setEntries] = useState({});

  useEffect(() => {
    if (!campaignId) return undefined;
    setEntries({});
    const nodeRef = ref(database, `campaign_regen/${campaignId}`);
    const unsubscribe = onValue(
      nodeRef,
      (snapshot) => setEntries(snapshot.val() || {}),
      () => setEntries({}) // permission/network error -> just show nothing
    );
    return () => unsubscribe();
  }, [campaignId]);

  return useMemo(() => {
    const keys = new Set();
    const now = Date.now();
    Object.values(entries || {}).forEach((e) => {
      if (!e || !e.cam_step_id || !e.email) return;
      if (e.started_at && now - e.started_at > STALE_MS) return; // leftover
      keys.add(`${e.cam_step_id}|${String(e.email).toLowerCase()}`);
    });
    return keys;
  }, [entries]);
};

export default useRegeneratingSteps;
